import { useRef, useState } from "react";
import { Download, ChevronDown, Package, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { prototypeApi, apiErrorMessage } from "@/lib/prototypeApi";

function VideoCard({ video, index }) {
  const [open, setOpen] = useState(false);
  const clips = Array.isArray(video.clips) ? video.clips : [];
  return (
    <div className="rounded-2xl border border-re-line bg-white p-4" data-testid={`video-card-${index}`}>
      <div className="mb-3 flex items-center justify-between">
        <span className="font-heading text-sm font-semibold text-re-charcoal">Video {index + 1}</span>
        <Badge variant={video.status === "success" ? "default" : "secondary"}>{video.status || "—"}</Badge>
      </div>
      {video.url ? (
        <video src={video.url} controls playsInline className="w-full rounded-xl border border-re-line" data-testid={`video-player-${index}`} />
      ) : (
        <div className="flex aspect-video items-center justify-center rounded-xl border border-dashed border-re-line text-sm text-re-muted">
          {typeof video.progress === "number" ? `Bezig… ${video.progress}%` : "Nog geen video beschikbaar"}
        </div>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {video.url && (
          <a href={video.url} target="_blank" rel="noreferrer" download data-testid={`video-download-${index}`}>
            <Button size="sm" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink">
              <Download className="mr-1.5 h-4 w-4" /> Download video
            </Button>
          </a>
        )}
        {clips.length > 0 && (
          <Collapsible open={open} onOpenChange={setOpen} className="w-full">
            <CollapsibleTrigger asChild>
              <Button size="sm" variant="outline" className="rounded-full" data-testid={`video-clips-toggle-${index}`}>
                {clips.length} losse clips <ChevronDown className={`ml-1.5 h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {clips.map((c, ci) => (
                <a key={ci} href={c} target="_blank" rel="noreferrer" download className="flex items-center gap-2 rounded-lg border border-re-line px-3 py-2 text-xs text-re-charcoal hover:border-re-red/40" data-testid={`clip-download-${index}-${ci}`}>
                  <Download className="h-3.5 w-3.5 text-re-red" /> Clip {ci + 1}
                </a>
              ))}
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </div>
  );
}

export function VideosPanel({ listingId, videos }) {
  const [exporting, setExporting] = useState(false);
  const [exportState, setExportState] = useState(null); // {status, progress, total, downloadUrl, filename, error}
  const [error, setError] = useState("");
  const timer = useRef(null);

  const pollExport = (jobId) => {
    const tick = async () => {
      try {
        const { data } = await prototypeApi.exportStatus(jobId);
        setExportState(data);
        if (data.status === "ready" || data.status === "error") {
          setExporting(false);
          return;
        }
      } catch (err) {
        setError(apiErrorMessage(err));
        setExporting(false);
        return;
      }
      timer.current = setTimeout(tick, 4000);
    };
    tick();
  };

  const startExport = async () => {
    setError("");
    setExporting(true);
    setExportState({ status: "pending", progress: 0 });
    try {
      const { data } = await prototypeApi.exportListing(listingId);
      if (data.downloadUrl) {
        setExportState({ status: "ready", downloadUrl: data.downloadUrl });
        setExporting(false);
      } else if (data.jobId) {
        pollExport(data.jobId);
      } else {
        setExporting(false);
      }
    } catch (err) {
      setError(apiErrorMessage(err));
      setExporting(false);
    }
  };

  const es = exportState || {};

  return (
    <div className="space-y-5" data-testid="videos-panel">
      <div className="rounded-2xl border border-re-line bg-re-soft p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-re-red" />
            <div>
              <p className="font-heading text-sm font-semibold text-re-charcoal">Alle video&apos;s als ZIP</p>
              <p className="text-xs text-re-muted">Bevat alle finale renders en losse clips.</p>
            </div>
          </div>
          <Button onClick={startExport} disabled={exporting} data-testid="export-button" className="rounded-full bg-re-red text-white hover:bg-re-deepred">
            {exporting ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />}
            Download alle video&apos;s
          </Button>
        </div>

        {exportState && (
          <div className="mt-4" data-testid="export-state">
            {(es.status === "pending" || es.status === "building") && (
              <>
                <p className="text-sm text-re-muted">Export wordt voorbereid… ({es.status})</p>
                {typeof es.total === "number" && es.total > 0 && (
                  <Progress value={Math.round(((es.progress || 0) / es.total) * 100)} className="mt-2 h-2" />
                )}
              </>
            )}
            {es.status === "ready" && es.downloadUrl && (
              <div>
                <a href={es.downloadUrl} target="_blank" rel="noreferrer" download data-testid="export-download-link">
                  <Button size="sm" className="rounded-full bg-re-charcoal text-white hover:bg-re-ink">
                    <Download className="mr-1.5 h-4 w-4" /> ZIP downloaden{es.filename ? `: ${es.filename}` : ""}
                  </Button>
                </a>
                <p className="mt-2 text-xs text-re-muted">Let op: deze downloadlink kan tijdelijk zijn.</p>
              </div>
            )}
            {es.status === "error" && (
              <p className="text-sm text-re-red">Export mislukt: {es.error || "onbekende fout"}.</p>
            )}
          </div>
        )}
        {error && <p className="mt-3 text-sm text-re-red">{error}</p>}
      </div>

      <p className="text-sm text-re-muted" data-testid="videos-count">
        {videos.length} video{videos.length === 1 ? "" : "'s"} ontvangen van Rendy.
      </p>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {videos.map((v, i) => (
          <VideoCard key={v.id || i} video={v} index={i} />
        ))}
      </div>
    </div>
  );
}
